const INTERNAL_MESSAGE_TYPE = 'internal';

export class UwsClient {
    constructor(endpoint, auth_endpoint, window_id, onAppOpen, onAppClose, onAppMessage, onControlMessage = null) {
        this.endpoint = endpoint;
        this.auth_endpoint = auth_endpoint;

        this.window_id = window_id;

        this.onAppOpen = onAppOpen;
        this.onAppClose = onAppClose;
        this.onAppMessage = onAppMessage;
        this.onControlMessage = onControlMessage;

        this.maxRetries = 1;
        this.retryBaseDelay = 1000;

        this.reset()
    }

    reset() {
        this.websocket = null;
        this.connected = false;
        this.websocket_id = null;
    }

    isConnected() {
        return this.connected;
    }

    connect() {
        if (this.isConnected()) {
            console.debug('UWS: Connect requested, but already connected.');
            return;
        }

        this.websocket = new WebSocket(this.endpoint);
        this.websocket.onopen = this.onOpen.bind(this);
        this.websocket.onmessage = this.onMessage.bind(this);
        this.websocket.onclose = this.onClose.bind(this);
        this.websocket.onerror = this.onError.bind(this);
    }

    disconnect(code) {
        if (!code) {
            code = 4500;
        }
        if (this.websocket === null) {
            console.debug('UWS: Close requested, but there is no connection.');
            return;
        }

        switch (this.websocket.readyState) {
            case this.websocket.CLOSING:
                console.debug('UWS: Close requested, but connection already closing.');
                break;
            case this.websocket.CLOSED:
                console.debug('UWS: Close requested, but connection is already closed.');
                break;
            case this.websocket.CONNECTING:
            case this.websocket.OPEN:
                // Will be reset through the close handler
                this.websocket.close(code, 'bye');
                break;
        }
    }

    onOpen() {
        console.debug('UWS: Connection opened.');
    }

    onMessage(event) {
        const message = JSON.parse(event.data);
        switch (message.type) {
            case 'need_auth':
                console.debug('UWS: Received login request.');
                this.handle_auth({
                    wid: this.window_id,
                    ...message
                });
                break;
            case 'login_ok':
                console.debug('UWS: Login OK.');
                this.connected = true;
                this.onAppOpen();
                break;
            case 'topic_token':
                console.debug('UWS: Server needs token to subscribe');
                this.handle_topic_auth(message);
                break;
            case 'sub_ok':
                // Subscription control ACK. Keep this out of onAppMessage so the
                // realtime wrapper can settle subscribe callbacks by topic.
                console.debug(`UWS: Subscribed to ${message.topic}`);
                this.onControlMessage?.(message);
                break;
            case 'sub_rejected':
                // Subscription control error, paired with sub_ok above.
                console.error('UWS: Server rejected subscribe', message);
                this.onControlMessage?.(message);
                break;
            default:
                if (this.shouldLogMessage(message)) {
                    console.debug('UWS: message', message);
                }
                this.onAppMessage(message);
        }

    }

    shouldLogMessage(message) {
        return !this.isInternalProtocolMessage(message);
    }

    isInternalProtocolMessage(message) {
        return message?.message?.type === INTERNAL_MESSAGE_TYPE;
    }

    onClose(event) {
        if (event.code >= 4000 && event.code <= 4999) {
            if (event.code >= 4500) {
                console.debug(`UWS: Connection closed by client (${event.code}).`);
            } else {
                console.debug(`UWS: Connection closed by server (${event.code})`);
            }
        } else {
            console.debug(`UWS: Connection closed abruptly (${event.code}).`);
        }
        this.reset();
        this.onAppClose(event.code, event.reason);
    }

    onError(event) {
        console.debug('UWS: error', event);
    }

    handle_auth(message) {
        const self = this;
        self.postRequest(
            self.auth_endpoint,
            message,
            (resp) => {
                self.updateConnectionId(message.id);
                self.send_auth_response(resp.signature, resp.userdata);
            },
            (err) => console.error('UWS: Login endpoint rejected request', err),
        );
    }

    handle_topic_auth(message) {
        const self = this;
        self.postRequest(
            self.auth_endpoint,
            { ...message, id: self.websocket_id },
            (resp) => self.sub_token(resp.topic, resp.token),
            (err) => console.error('UWS: Topic endpoint rejected request', err),
        );
    }

    send(obj, from) {
        if (this.websocket === null) {
            console.debug(`UWS: Ignoring ${from}, no active connection`);
            return;
        }
        this.websocket.send(JSON.stringify(obj));
    }

    subscribe(topic) {
        this.send({
            action: 'sub',
            topic: topic,
        }, 'subscribe');
    }

    unsubscribe(topic) {
        this.send({
            action: 'unsub',
            topic: topic,
        }, 'unsubscribe');
    }

    sub_token(topic, token) {
        this.send({
            action: 'sub_token',
            topic: topic,
            token: token,
        }, 'sub_token');
    }

    send_auth_response(signature, userdata) {
        this.send({
            action: 'login',
            signature: signature,
            userdata,
        }, 'send_auth_response');
    }

    send_typing(pageId, discussionId, commentId) {
        this.send({
            action: 'typing',
            pageId,
            discussionId,
            commentId,
        }, 'send_typing');
    }

    updateConnectionId(id) {
        this.websocket_id = id;
    }

    postRequest(uri, data, success, error, attempt = 1) {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", uri, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
                const resp = JSON.parse(xhr.response);
                if (resp.status === 'ok') {
                    success(resp)
                } else {
                    error(xhr.response);
                }
            } else if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 0) {
                // Handle network errors, such as ERR_NETWORK_CHANGED when client wakes up from sleep
                if (attempt > this.maxRetries) {
                    console.debug(`UWS: Max retries reached, aborting.`);
                    return;
                }
                const delay = Math.round(this.retryBaseDelay * Math.pow(1.5, attempt-1));
                console.debug(`UWS: Request failed, retry ${attempt}/${this.maxRetries} in ${delay}ms`);
                setTimeout(this.postRequest.bind(this), delay, uri, data, success, error, attempt+1);
            }
        };

        xhr.send(JSON.stringify(data));
    }
}
